import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Workspace } from '@/data'
import { todayIn, type BusinessCalendar, type DateOnly } from '@/domain/dates'
import {
  buildCalendar,
  can as canRole,
  computeBalance,
  legalRuleStatus,
  supervisedIds,
  type Balance,
  type ChangeRequest,
  type CustomDay,
  type Department,
  type Employee,
  type LeaveRequest,
  type LegalRuleStatus,
  type Permission,
} from '@/domain/vacations'

/**
 * Derived, memoised view of the organization's data for the screens: calendar, today,
 * balances and "what can I do". Recomputed only when the workspace query changes.
 */
export interface Ws {
  data: Workspace
  orgId: string
  today: DateOnly
  year: number
  calendar: BusinessCalendar
  can: (p: Permission) => boolean
  me: Employee | null
  employee: (id: string) => Employee | undefined
  department: (id: string | null) => Department | undefined
  /** People I supervise (direct reports for managers, everyone for HR/admin). */
  supervised: Set<string>
  balanceOf: (employeeId: string, year?: number) => Balance
  legalOf: (employeeId: string, year?: number) => LegalRuleStatus
  bridges: CustomDay[]
  /** Requests and change requests waiting for MY decision. */
  toDecide: { requests: LeaveRequest[]; changes: ChangeRequest[] }
  awayOn: (day: DateOnly, ids?: Set<string>) => LeaveRequest[]
}

const Ctx = createContext<Ws | null>(null)

export function WorkspaceProvider({ data, children }: { data: Workspace; children: ReactNode }) {
  const value = useMemo<Ws>(() => {
    const settings = data.organization.settings
    const today = todayIn(settings.timezone)
    const year = Number(today.slice(0, 4))
    const calendar = buildCalendar(data.customDays)
    const byId = new Map(data.employees.map((e) => [e.id, e]))
    const depts = new Map(data.departments.map((d) => [d.id, d]))
    const me = data.me
    const supervised = supervisedIds(data.role, me?.id ?? null, data.employees)
    const cache = new Map<string, Balance>()
    const balanceOf = (employeeId: string, y = year) => {
      const key = `${employeeId}:${y}`
      let b = cache.get(key)
      if (!b) {
        const employee = byId.get(employeeId) ?? { id: employeeId, annualDays: null }
        b = computeBalance({
          employee,
          year: y,
          requests: data.requests,
          record: data.balanceRecords.find((r) => r.employeeId === employeeId && r.year === y),
          settings,
          calendar,
          today,
        })
        cache.set(key, b)
      }
      return b
    }
    const mayDecide = (employeeId: string) =>
      data.role === 'admin' || (data.role === 'manager' && employeeId !== me?.id && byId.get(employeeId)?.managerId === me?.id)
    return {
      data,
      orgId: data.organization.id,
      today,
      year,
      calendar,
      can: (p) => canRole(data.role, p),
      me,
      employee: (id) => byId.get(id),
      department: (id) => (id ? depts.get(id) : undefined),
      supervised,
      balanceOf,
      legalOf: (employeeId, y = year) => legalRuleStatus(data.requests, employeeId, y, settings.minConsecutiveDays),
      bridges: data.customDays.filter((d) => d.kind === 'bridge'),
      toDecide: {
        requests: data.requests.filter((r) => r.status === 'pending' && mayDecide(r.employeeId)).sort((a, b) => (a.start < b.start ? -1 : 1)),
        changes: data.changeRequests.filter((c) => c.status === 'pending' && mayDecide(c.employeeId)),
      },
      awayOn: (day, ids) =>
        data.requests.filter((r) => (r.status === 'approved' || r.status === 'pending') && r.start <= day && r.end >= day && (!ids || ids.has(r.employeeId))),
    }
  }, [data])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useWs(): Ws {
  const v = useContext(Ctx)
  if (!v) throw new Error('useWs fora do WorkspaceProvider')
  return v
}
